/**
 * Customer Club popup controller.
 *
 * Loaded by the "Customer Club popup" section (customer-club-signup-popup.liquid).
 * Both the signup form and the login form are rendered server-side inside their
 * <dialog>s by the section, so their scripts run natively at page load (CSP-safe).
 * This controller opens/closes the dialogs, shows the post-signup success message,
 * and (when possible) keeps login errors inside the login popup.
 *
 * Triggers (event delegation):
 *   signup: [data-club-popup-open]  or  a[href="#customer-club-signup"]
 *   login:  [data-club-login-open]  or  a[href="#customer-club-login"]
 *   close:  [data-club-popup-close]  (closes the dialog it belongs to)
 */
(function () {
  if (window.__customerClubPopupInit) return;
  window.__customerClubPopupInit = true;

  var SUCCESS_KEY = 'cc_club_welcome';

  function init() {
    var config = document.querySelector('[data-club-popup-config]');
    if (config && config.getAttribute('data-is-member') === 'true') {
      // Hide standalone signup links/buttons for members (e.g. links in rich text).
      document.body.classList.add('is-club-member');
    }
    if (config && config.getAttribute('data-is-logged-in') === 'true') {
      // Hide login links for any logged-in customer — they don't need to log in.
      document.body.classList.add('is-logged-in');
    }

    var signupDialog = document.getElementById('customer-club-popup');
    var loginDialog = document.getElementById('customer-login-popup');
    var successEl = signupDialog ? signupDialog.querySelector('[data-club-popup-success]') : null;
    var formEl = signupDialog ? signupDialog.querySelector('[data-club-popup-form]') : null;

    // ---- Scroll lock (the real "jump to top" fix) ----------------------------
    // The theme runs its own handler on in-page anchor links (#...) that calls
    // window.scrollTo — a PROGRAMMATIC scroll that preventDefault() cannot stop.
    // Instead of fighting it, we pin <body> with position:fixed while the popup
    // is open, so nothing (browser or theme) can move the visible page. We store
    // the scroll position on mousedown/touchstart (BEFORE the click fires) so we
    // capture it even if the theme scrolls to the top during the same click.
    var savedScrollY = 0;
    var preClickScroll = null;

    function snapshotScroll() {
      preClickScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
    }
    document.addEventListener('mousedown', snapshotScroll, true);
    document.addEventListener('touchstart', snapshotScroll, true);

    function lockScroll() {
      if (document.body.classList.contains('cc-scroll-locked')) return;
      savedScrollY = preClickScroll != null
        ? preClickScroll
        : (window.pageYOffset || document.documentElement.scrollTop || 0);
      preClickScroll = null;
      // Set the locking styles INLINE (not via a CSS class) so the lock can never
      // fail silently if the snippet's CSS isn't the latest version. This is the
      // whole "jump to top" fix: <body> is pinned, so neither the browser nor the
      // theme's anchor-scroll script can move the visible page.
      var s = document.body.style;
      // Compensate for the scrollbar that disappears when <body> is fixed.
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) s.paddingRight = sbw + 'px';
      s.position = 'fixed';
      s.left = '0';
      s.right = '0';
      s.width = '100%';
      s.overflow = 'hidden';
      s.top = (-savedScrollY) + 'px';
      document.body.classList.add('cc-scroll-locked');
    }

    function unlockScroll() {
      if (!document.body.classList.contains('cc-scroll-locked')) return;
      // Temporarily disable smooth scrolling so restoring the position is instant
      // (a themed `scroll-behavior: smooth` would otherwise animate it = a visible hop).
      var html = document.documentElement;
      var prevBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      document.body.classList.remove('cc-scroll-locked');
      var s = document.body.style;
      s.position = '';
      s.left = '';
      s.right = '';
      s.width = '';
      s.overflow = '';
      s.top = '';
      s.paddingRight = '';
      window.scrollTo(0, savedScrollY);
      html.style.scrollBehavior = prevBehavior;
    }

    function openDialog(dialog) {
      if (!dialog || dialog.open) return;
      // Join mode (logged-in non-member): pre-check the club opt-in.
      if (dialog.classList.contains('customer-club-popup--join')) {
        var clubToggle = dialog.querySelector('#toggle-customerclub');
        if (clubToggle) clubToggle.checked = true;
      }
      lockScroll();
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    function closeDialog(dialog) {
      if (!dialog) return;
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
      unlockScroll();
    }

    function showSuccess() {
      if (!successEl) return;
      if (formEl) formEl.hidden = true;
      successEl.hidden = false;
    }

    // ---- Login: keep errors inside the popup when possible -------------------
    // Native Shopify login shows errors on /account/login. We POST via fetch so
    // wrong-password errors can be shown inline instead. If the store enforces
    // captcha on forms, AJAX can't pass it, so we fall back to a native submit.
    function setupInlineLogin(dialog) {
      if (!dialog) return;
      var loginForm = dialog.querySelector('form');
      if (!loginForm) return;

      loginForm.addEventListener('submit', function (e) {
        // Captcha enforced -> let Shopify handle the native submit.
        if (window.Shopify && window.Shopify.captcha) return;

        e.preventDefault();
        clearLoginError(loginForm);
        var btn = loginForm.querySelector('button[type="submit"], button:not([type])');
        if (btn) btn.disabled = true;

        fetch(loginForm.action, {
          method: 'POST',
          body: new FormData(loginForm),
          headers: { 'X-Requested-With': 'fetch' }
        })
          .then(function (r) {
            return r.text().then(function (html) {
              return { url: r.url || '', redirected: r.redirected, html: html };
            });
          })
          .then(function (res) {
            var onLoginPage = res.url.indexOf('/account/login') !== -1;
            if (res.redirected && !onLoginPage) {
              // Logged in -> reload so the current page reflects the session.
              window.location.reload();
              return;
            }
            // Not logged in -> show the error from the returned page.
            showLoginError(loginForm, extractLoginError(res.html));
            if (btn) btn.disabled = false;
          })
          .catch(function () {
            // Anything unexpected -> fall back to a normal submit.
            loginForm.submit();
          });
      });
    }

    function extractLoginError(html) {
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var alert = doc.querySelector('#login .alert, .text-error-text');
        if (alert) return alert.textContent.trim();
      } catch (e) {}
      return null;
    }

    function clearLoginError(form) {
      var el = form.querySelector('[data-login-error]');
      if (el) el.remove();
    }

    function showLoginError(form, message) {
      var el = form.querySelector('[data-login-error]');
      if (!el) {
        el = document.createElement('div');
        el.setAttribute('data-login-error', '');
        el.className = 'alert mb-8 bg-error-bg text-error-text';
        el.setAttribute('tabindex', '-1');
        form.insertBefore(el, form.firstChild);
      }
      el.textContent = message || 'Forkert mail eller adgangskode.';
      el.focus();
    }
    // -------------------------------------------------------------------------

    setupInlineLogin(loginDialog);

    // Detect a successful signup via the dataLayer event the form pushes.
    // Flag it in sessionStorage so the message can be re-shown after the
    // post-signup login redirect brings the customer back to this page.
    var dl = (window.dataLayer = window.dataLayer || []);
    var originalPush = dl.push;
    dl.push = function () {
      for (var i = 0; i < arguments.length; i++) {
        var entry = arguments[i];
        if (entry && entry.event === 'customer_club_signup') {
          try { sessionStorage.setItem(SUCCESS_KEY, '1'); } catch (e) {}
          showSuccess();
          openDialog(signupDialog);
        }
      }
      return originalPush.apply(this, arguments);
    };

    // Capture phase (3rd arg = true): run BEFORE any bubble-phase handler in the
    // theme that might call stopPropagation() on the click. Otherwise the anchor's
    // default (#customer-club-signup) fires and the page jumps to the top.
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-club-popup-open], a[href="#customer-club-signup"]')) {
        e.preventDefault();
        openDialog(signupDialog);
        return;
      }
      if (e.target.closest('[data-club-login-open], a[href="#customer-club-login"]')) {
        e.preventDefault();
        openDialog(loginDialog);
        return;
      }
      var closeBtn = e.target.closest('[data-club-popup-close]');
      if (closeBtn) {
        e.preventDefault();
        closeDialog(closeBtn.closest('dialog'));
      }
    }, true);

    [signupDialog, loginDialog].forEach(function (dialog) {
      if (!dialog) return;
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) closeDialog(dialog); // backdrop click
      });
      // Esc key closes a native <dialog> without firing our close handler.
      dialog.addEventListener('close', function () {
        unlockScroll();
      });
    });

    // Just signed up (and got logged in via redirect)? Show the welcome message.
    var welcomed = false;
    try { welcomed = sessionStorage.getItem(SUCCESS_KEY) === '1'; } catch (e) {}
    if (welcomed) {
      try { sessionStorage.removeItem(SUCCESS_KEY); } catch (e) {}
      showSuccess();
      openDialog(signupDialog);
    } else if (window.location.hash === '#customer-club-signup') {
      openDialog(signupDialog);
    } else if (window.location.hash === '#customer-club-login') {
      openDialog(loginDialog);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
